import React from 'react';
import { Compass, ChevronRight, Volume2, ArrowRight, ArrowLeft, Globe, FileText, ExternalLink } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { ArchiveTab } from '../../types';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface SectionProps {
  onNavigate?: (tab: ArchiveTab) => void;
}

export const WhyTheyCameSection: React.FC<SectionProps> = ({ onNavigate }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-16 font-sans text-[#2c221e]">
      
      {/* Header & Breadcrumbs & Voice Reader */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {onNavigate && (
              <button
                onClick={() => onNavigate('journey')}
                className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
              >
                <Compass className="w-3.5 h-3.5" />
                <span>The Journey</span>
              </button>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Why they came around the curve of the world
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs"
            title="Listen to Why they came around the curve of the world"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          WHY THEY CAME AROUND THE CURVE OF THE WORLD
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Globe className="w-3.5 h-3.5" />
          <span>Colonial Motivations, Voyage Realities & Canterbury Settlement</span>
        </div>
      </header>

      {/* Main Archival Document Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Intro Paragraphs */}
        <div className="space-y-4">
          <p>
            From the distance of several generations, we cannot be completely certain why people chose to leave their homelands to settle in a faraway place and change their lives forever. The Dentons came in a planned, albeit with short notice, and organized way. Many others came on the emigrant ships, leaving behind bleak prospects and ready to make better lives for themselves……and they did so in many fields.
          </p>

          <p>
            The Gibbs came from the Angus Glens as sought-after shepherds. The Giles left Somerset for planned prosperous futures in agricultural contracting with modern machines. Some came as assisted emigrants to find ready employment. Robert Morris Roberts left a ship perhaps as an adventurer who liked the look at the land. Sarah Baxter came from Ireland for health reasons as a 15-year-old and lived for another 80 years.
          </p>

          <p>
            Family historians have suggested a number of reasons why John Frederick (54) and Anne Maria (37) Denton packed up, and with their seven children, the eldest being 16, left Suffolk where the family had farmed for more than 150 years. They had lived in some comfort with the prior census showing the household included three indoor servants and a ratcatcher.
          </p>
        </div>

        {/* Reasons Bullet Points */}
        <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
          <ul className="list-disc list-outside pl-6 space-y-2.5 text-[#2c221e]">
            <li>They were influenced and encouraged by John Denton’s friend Colonel Robert Rushbrook, MP for West Sussex, who was deeply interested in the development of agriculture in the new lands.</li>
            <li>John and Anne Denton were very likely influenced by the Rev Charles Martin Torlesse whose wife Catherine was the eldest sister of Edward Gibbon Wakefield<sup>1</sup>, and who was minister at Stoke-by-Nayland for more than 50 years. The Dentons were very involved in his parish.</li>
            <li>They had five sons who were likely to grow up in relatively depressed times in rural England with little hope of getting their own good-sized farms but they could see that was possible through the Canterbury Association which had 1.25 million hectares available.</li>
            <li>The offer of assistance and support for agricultural workers to emigrate from the Canterbury Association Land Fund made the long voyage affordable for the older sons.</li>
            <li>As a colonist John Denton was able to purchase farmland and he anticipated that by taking a wide range of brightly painted red and blue farm equipment, seedlings and a wide range of grains and seeds</li>
          </ul>
        </div>

        {/* Social & Economic Conditions in 1840s Britain */}
        <div className="space-y-4">
          <p>
            The Dentons would have seen how rural England was facing a time of great change. The levels of urban poverty in towns and cities were appalling, and well portrayed in novels such as Charles Dickens book “Oliver Twist”. Housing was generally very poor and unhealthy, education limited and wages inadequate. For many in the towns and cities there was an alternative to the dreaded workhouse and that was emigration where domestic servants and labourers were given assisted passage to jobs, real opportunities, and an escape from the class system. Workers with skills were able to soon own their own businesses for example.
          </p>

          <p>
            Cottage industries that had supported families were being replaced by factories. Textiles and lace were more cheaply produced by machines. The people who had made hand-stitched boots, potters, furniture makers, brewers and millers lost their livelihoods to the industrial revolution. Those skills were in great demand in the young settlements. In Scotland the highland clearances and fencing of the commons turned farmers into crofters but the industries they then worked in, such as fishing, quarrying and the kelp industry, were not long term or proper replacement to traditional ways of life on the land. And then came the failure of the potato crops and the subsequent famine saw more than a million people leave Ireland and Scotland in particular.
          </p>

          <p>
            Against that, the new societies in USA, Canada, the Australian States and NZ became realistic and achievable alternatives. Their governments actively sought families and women and gave subsidised transport and support when they arrived. Single women now had options and control over their futures, although they were often married not long after they arrived and were far outnumbered by men in the colonies.
          </p>
        </div>

        {/* William Deans Quote */}
        <blockquote className="my-6 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
          <p className="italic">
            William Deans wrote back to his father in Scotland as early as March 1841 advising that two of his brothers “should come out here and as soon as possible. If they were to be farmers at home they would probably make nothing by it, while here their success with common prudence is certain. They can buy as good land here at 1 Pound per acre from the Government as they can rent in Scotland for 2 Pounds, with a ready and certain market for their produce at prices double those at home, and they will live in a beautiful and healthy climate where there are no taxes on native produce, and free from political discord.”<sup>2</sup>
          </p>
        </blockquote>

        <p>
          Ship conditions varied but the journey was at least 75 days in cramped conditions and uncertain weather.
        </p>

        {/* Image 27: Steerage */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/27 Steerage.jpg"
            alt="Steerage on an emigrant ship"
            caption="Steerage on an emigrant ship (these two images from the First Four Ships Centennial Souvenir Booklet)"
            filenameHint="27 Steerage.jpg"
          />
          <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
            Steerage on an emigrant ship (these two images from the First Four Ships Centennial Souvenir Booklet)
          </p>
        </div>

        {/* Image 28: Forecabin passengers */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/28 forecabin passengers.jpg"
            alt="Forecabin passengers"
            caption="John and Anne Denton and some of their children travelled in fore cabin as Colonists on Sir George Seymour and had relative comfort on the 99-day voyage."
            filenameHint="28 forecabin passengers.jpg"
          />
          <p className="font-serif text-base md:text-lg leading-relaxed text-[#2c221e]">
            John and Anne Denton and some of their children travelled in fore cabin as Colonists on Sir George Seymour and had relative comfort on the 99-day voyage.
          </p>
        </div>

        <p>
          There were four cows on Sir George Seymour including a prized shorthorn, which supplied fresh milk, owned by Willam Guise Brittan.
        </p>

        {/* Image 29: Shorthorn cow */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/29 Shorthorn cow.jpg"
            alt="Shorthorn cow"
            caption="Shorthorn cow"
            filenameHint="29 Shorthorn cow.jpg"
          />
          <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
            Shorthorn cow
          </p>
        </div>

        <p>
          But on 28 December 1850 a servant let the valuable cow wander up the hillside but after 100 days at sea her legs were still weak. She stumbled, rolled down the hillside and over a cliff to her death. Of the five cows that came out in the first four ships, only two survived into 1851.
        </p>

        <p>
          In the decades of the 1850s and 1860s the newcomers arrived at the small port of Lyttelton and the rudimentary Christchurch.
        </p>

        {/* Image 30: Lyttelton 1852 */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/30 Lyttelton 1852.jpg"
            alt="Lyttelton 1852 by Janetta Maria Cookson"
            caption="Lyttelton 1852 by Janetta Maria Cookson"
            filenameHint="30 Lyttelton 1852.jpg"
          />
          <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
            Lyttelton 1852 by Janetta Maria Cookson
          </p>
        </div>

        {/* Image 31: Avon River */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/31 Avon River.jpg"
            alt="Avon River with Worcester Street bridge, 1860"
            caption="Avon River with Worcester Street bridge, 1860 (Photos: Christchurch Star Archives & Christchurch City Libraries)"
            filenameHint="31 Avon River.jpg"
          />
          <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
            Avon River with Worcester Street bridge, 1860 (Photos: Christchurch Star Archives &amp; Christchurch City Libraries)
          </p>
        </div>

        {/* Image 31A: Central Christchurch 1865 */}
        <div className="space-y-2 my-6">
          <ArchivalImageCard
            src="/images/31A Central Christchurch 1865.jpg"
            alt="Central Christchurch 1865"
            caption="Central Christchurch 1865"
            filenameHint="31A Central Christchurch 1865.jpg"
          />
          <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
            Central Christchurch 1865
          </p>
        </div>

        {/* Population of Christchurch */}
        <div className="space-y-4 pt-2">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              POPULATION OF CHRISTCHURCH
            </h2>
          </div>
          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>In 1854 there were 924 people living in Christchurch.</li>
              <li>In 1866 there were 6500 people and 65 hotels.</li>
              <li>In 1903 there were 54,041 people and it was the second largest town in New Zealand.</li>
            </ul>
          </div>
        </div>

        {/* Charlotte Godley Quote */}
        <div className="space-y-3 pt-2">
          <p className="font-serif font-semibold text-[#4a3525]">
            Charlotte Godley wrote about the settlement’s rapid development on 14 January 1852:
          </p>
          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
            <p className="italic">
              “We went along nearly five miles of excellent road into Christchurch, a new since I had been on the Plains, and, much as I had heard, I was very much surprised, and not a little delighted, to see how very civilized the country had become. There are houses, gardens and cultivation, in sight all the way along; the patches, in some places, quite touching each other, and although the houses are small, they are tidy and weathertight looking places. All the people, too, doing so well, and looking so contented, and then when we got to the town, the number of houses seemed quite incredible to me, and the whole place so cheerful and busy, that it has quite lost the dreary look which I own thought it had last year.”
            </p>
          </blockquote>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Another Perspective of the Journey Out */}
        <div className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              AND ANOTHER PERSPECTIVE OF THE JOURNEY OUT<sup>3</sup>
            </h2>
          </div>

          <p>
            In many reports there has been a glossing over of the voyage out, the most favourable impression created. That view is countered somewhat by William Guise Brittan who came out on Sir George Seymour and took over as the ship’s surgeon.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-3 shadow-xs">
            <p className="italic">
              “For the first week the shop rolled horribly and all the passengers suffered from seasickness, including the chaplain and surgeons. As the weather improved the decks became more animated and Cyrus Davie recorded in his diary:” Ladies working, Gentlemen reading, Chess playing, Chart consulting, General conversation, etc
            </p>
            <p className="italic">
              ”As the days grew warmer, the gentlemen began a morning bathing ritual, wearing loose drawers while being doused with buckets of water. In the evening glee singers sang in the moonlight while the steerage passengers danced on the forecastle deck.”
            </p>
          </blockquote>

          <p>
            Two infants died and their bodies were committed to the sea.
          </p>

          <p>
            There was considerable friction between the Captain Goodson and the ship’s surgeon<sup>4</sup> Wilkinson. William Brittan said neither seemed willing to work together for the comfort and well-being of the passengers. He thought Wilkinson was incompetent, inefficient and lost his temper over trifles while the captain seemed determined not to carry out the strict and literal instructions of the Charter for the daily allowance of food.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-3 shadow-xs">
            <p className="italic">
              “Complaints became rife amongst all classes of passengers, Steerage, Intermediate and Chief cabin, that their dietary was not properly supplied and their requests for reasonable comforts were ignored. Wilkinson exaggerated their complaints until at length matters were brough to such a pass that to prevent a serious outbreak on the part of the steerage passengers Brittan intervened and relieved Wilkinson of his authority as superintendent of emigrants. All complaints were to be directed to him, as chairman of the Colonists’ Society, and he alone would negotiate with the captain.”
            </p>
            <p className="italic">
              “Within a short time Brittan wrote, a little address and conciliation succeeded in soothing the highly irritated state of feeling that prevailed throughout the ship, and the people were brought to a more orderly and satisfied state. From this time, he personally supervised the distribution of provisions in the hold, and there were no more complaints from steerage………………… The cooks and stewards were inefficient and unsatisfactory, which added further to the passengers’ discomfort.”
            </p>
          </blockquote>

          <p>
            Brittan was also greatly annoyed that the captain was indifferent to the Canterbury Association’s instructions for regular daily church services and weekly communion. “Not only did the captain refuse to clear a space for the divine service, but on many occasions offered an offensive obstruction to its performance.”
          </p>

          <p>
            The sacked surgeon then had a dalliance with one of the single women in steerage, and with the lack of privacy on board, many people observed the amorous couple. Brittan complained officially of this “criminal intimacy”, and the cabin class passengers were much embarrassed by the behaviour.
          </p>

          <p>
            In the cheerful reports in the Canterbury Papers nothing was said about the undoubted misery of the steerage passengers at times as they tried to keep warm and dry. Those passengers included two of the Denton boys.
          </p>

          {/* Image 31B: Sir George Seymour */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/31B Sir Georger Seymour.jpg"
              alt="Sir George Seymour"
              caption="Sir George Seymour, built 1844, abandoned at sea after fire broke out in the cargo, 18 December 1867. She was 43 m long, 10m beam, depth of 6.7m and 872 tonnes. Her maiden voyage was taking convicts to Australia, returning to the UK via Calcutta (now called Kolkata) in India where she dropped off 132 horses, passengers and other cargo."
              filenameHint="31B Sir Georger Seymour.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Sir George Seymour, built 1844, abandoned at sea after fire broke out in the cargo, 18 December 1867. She was 43 m long, 10m beam, depth of 6.7m and 872 tonnes. Her maiden voyage was taking convicts to Australia, returning to the UK via Calcutta (now called Kolkata) in India where she dropped off 132 horses, passengers and other cargo.
            </p>
          </div>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* It Might Have Been Wairarapa */}
        <div className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              IT MIGHT HAVE BEEN WAIARAPA AND NOT CANTERBURY
            </h2>
          </div>

          <p>
            In 1846 Governor George Grey waivered the Crown’s first option to buy and in the Waiarapa in favour of the New Zealand Company.
          </p>

          <p>
            In 1847 Francis Dillon Bell<sup>5</sup> was commissioned by the NZ Company to purchase land in the Waiarapa for the proposed Canterbury Settlement.
          </p>

          <p>
            By 1848 the government was seriously considering this would be the site for the “Church of England Canterbury Settlement”, a scheme to introduce a slice of English society to New Zealand. A meeting was held with the settlers and squatters already there, on 14 November 1848, to further discuss these matters. Separately there was a large meeting of Maori chiefs was held to discuss selling all the land they owned. There was a group of Maoris who seemed willing to sell a lot of land but there was another group who vehemently resisted any prosect of sale<sup>6</sup>.
          </p>

          {/* Image 32: Hunting pigs */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/32 Hunting pigs.jpg"
              alt="Maori and Europeans hunting pigs in the lower Waiarapa in 1847"
              caption="Maori and Europeans hunting pigs in the lower Waiarapa in 1847, a painting by Samuel Bries"
              filenameHint="32 Hunting pigs.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Maori and Europeans hunting pigs in the lower Waiarapa in 1847, a painting by Samuel Bries
            </p>
          </div>

          <p>
            In January 1849 it was recorded that 780 Māori lived in 10 valley and six coastal Waiarapa villages, permanent settlements with potato gardens and access to an abundance of feral pigs.
          </p>

          <p>
            There was also another consideration at play, put this way by Paul Goldsmith (in 1996)<sup>7</sup>:
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
            <p className="italic">
              “The prospect of Europeans coming, settling, working and improving the land, while Maori sat back and enjoyed an ever-increasing income from that work, presumably to become a wealthy indigenous aristocracy was unpalatable to many.”
            </p>
          </blockquote>

          <p>
            Attention turned to the vast tracts of land in Canterbury, and negotiations resulted in Kemp’s Deed<sup>8</sup> of 1848, signed on a sloop in Akaroa Harbour. In what would become a controversial deal, with some individuals not complying with the Crown directive to allow ample reserves for Ngai Tahu, the Crown purchased 13,551,400 acres of land for 2000 Pounds<sup>9</sup>.
          </p>

          {/* Image 33: Canterbury Association Map */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/33 Canterbury Association map.jpg"
              alt="Canterbury Association map"
              caption="Canterbury Association Map of the Canterbury Settlement"
              filenameHint="33 Canterbury Association map.jpg"
            />
          </div>

          <p>
            The Canterbury Association sold the land to the Canterbury Association for 10 shillings per acre (4000m²), which then sold the land to colonists for 3 Pounds per acre, reserving the 2 Pounds 10 shillings per acre for public good objectives such as roads, church and school endowments, and assisted emigration passages - especially for gardeners, shepherd, farm servants, labourers and country mechanics.
          </p>

          <p>
            William Guise Brittan was the Land Officer for the Canterbury Association<sup>10</sup>. There was a strict order for purchase, predetermined on lists which he enforced with efficiency.
          </p>

          {/* Note block */}
          <div className="bg-[#f3ede0] p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] text-[#4a3525] text-sm sm:text-base font-serif italic space-y-1 my-6">
            <p>
              <strong>NOTE:</strong> Views change over time on all these issues, including colonisation and land purchase, and it not within the realm of this collection of personal family stories to dip any toes into those waters. These are the stories of our family members who came to this country and committed themselves to making good in a land they came to love.
            </p>
          </div>
        </div>

        {/* Footnotes Section */}
        <footer className="pt-8 mt-10 border-t-2 border-[#d8ceb9] text-sm text-[#5c4328] space-y-3 font-serif">
          <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs text-[#7a5b3a]">
            <FileText className="w-4 h-4" />
            <span>Footnotes & References</span>
          </div>
          <ol className="list-decimal list-outside pl-5 space-y-1.5 text-xs sm:text-sm leading-relaxed">
            <li>Key figure in colonisation philosophy for NZ, Australia and Canada, active in NZ Association and Canterbury Settlement</li>
            <li>Letter from William Deans to his father, March 1841</li>
            <li>From “Cricketing Colonists, The Brittan brothers in Early Canterbury” by Geoffrey Rice &amp; Francis Ryman</li>
            <li>Surgeons were responsible for the conduct, morals, behaviour, nutrition and the health of passengers.</li>
            <li>Bell (1882 – 1898), agent for NZ Company and later a politician. His father was a cousin of Edward Gibbon Wakefield.</li>
            <li>
              <a 
                href="http://www.waihinga.ac.nz/chronology" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#7a5b3a] hover:underline inline-flex items-center gap-1"
              >
                <span>www.waihinga.ac.nz/chronology</span>
                <ExternalLink className="w-3 h-3 inline" />
              </a>
              {' '}by Professor Foss Leach (a Denton descendent)
            </li>
            <li>Goldsmith did research for the Waitangi Tribunal</li>
            <li>The boundaries were contentious from the start but there is a lot of research now available on this purchase.</li>
            <li>1 pound sterling in 1851 = 127.96 Pounds in 2021 values</li>
            <li>The system of land purchase is detailed in “Cricketing Colonists: The Brittan Brothers in Early Canterbury” by Rice &amp; Ryman.</li>
          </ol>
        </footer>

      </article>

      {/* Sub-page Navigation footer */}
      {onNavigate && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9]">
          <button
            onClick={() => onNavigate('journey-essentials')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif text-sm font-bold border border-[#d8ceb9] transition"
          >
            <ArrowLeft className="w-4 h-4 text-[#7a5b3a]" />
            <span>Previous: Essentials for Starting a New Life in NZ</span>
          </button>
          
          <button
            onClick={() => onNavigate('dentons')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif text-sm font-bold transition shadow-xs"
          >
            <span>Next: The Dentons</span>
            <ArrowRight className="w-4 h-4 text-amber-200" />
          </button>
        </div>
      )}

    </div>
  );
};
